import { Injectable } from '@angular/core';
import { IConfigVecino } from 'modelosSeguridad/src';
import * as XLSX from 'xlsx-js-style';

@Injectable({ providedIn: 'root' })
export class VecinosSeguridadExportService {
  public construirBlob(vecinos: IConfigVecino[]): Blob {
    const filas = vecinos.map((v) => ({
      Nombre: v.datosPersonales?.nombre ?? '-',
      DNI: v.datosPersonales?.dni ?? '-',
      Email: v.datosPersonales?.email ?? '-',
      Teléfono: v.datosPersonales?.telefono ?? '-',
      Sexo:
        v.datosPersonales?.sexo === true
          ? 'M'
          : v.datosPersonales?.sexo === false
            ? 'F'
            : '-',
      'Fecha Nacimiento': this._fmtDateOnly(v.datosPersonales?.fechaNacimiento),
      Dirección: v.direccion?.direccion ?? '-',
      'Complemento Dirección': v.direccion?.complementoDireccion ?? '-',
      Localidad: v.direccion?.localidad?.nombre ?? '-',
      Barrio: v.direccion?.barrio?.nombre ?? '-',
      Categoría:
        v.categoria?.categoria?.nombre ||
        v.cliente?.categoriaDefault?.nombre ||
        '-',
      'Categorizado Por': v.categoria?.usuario?.usuario ?? '-',
      Estado: v.activo ? 'Activo' : 'Inactivo',
      'Creado Por Admin': v.creadoPorAdmin ? 'Sí' : 'No',
      Importado: v.importado ? 'Sí' : 'No',
      'DNI Escaneado': v.dniEscaneado ? 'Sí' : 'No',
      'Fecha Alta': this._fmtDate(v.fechaCreacion),
      'Último Acceso': this._fmtDate(v.ultimoAcceso),
      App: v.app ?? '-',
      'Versión App': v.appVersion ?? '-',
      'Tipo App': v.appType ?? '-',
      Nota: v.nota ?? '-',
    }));

    const ws = XLSX.utils.json_to_sheet(filas);
    this._autoFitColumns(ws);
    this._applyHeaderStyle(ws);

    const buffer = XLSX.write(
      { Sheets: { Vecinos: ws }, SheetNames: ['Vecinos'] },
      { bookType: 'xlsx', type: 'array', cellStyles: true },
    );
    return new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  private _fmtDate(d?: string): string {
    if (!d) return '-';
    return new Date(d)
      .toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
      .replace(',', '');
  }

  private _fmtDateOnly(d?: string): string {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private _getPastelPrimaryRgb(): string {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--theme-primary-500')
      .trim();
    let r = 0,
      g = 0,
      b = 0;
    const hexMatch = raw.match(/^#?([0-9a-fA-F]{6})$/);
    if (hexMatch) {
      r = parseInt(hexMatch[1].slice(0, 2), 16);
      g = parseInt(hexMatch[1].slice(2, 4), 16);
      b = parseInt(hexMatch[1].slice(4, 6), 16);
    } else {
      const rgbMatch = raw.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        r = +rgbMatch[1];
        g = +rgbMatch[2];
        b = +rgbMatch[3];
      }
    }
    const mix = (c: number) => Math.round(c * 0.25 + 255 * 0.75);
    return [mix(r), mix(g), mix(b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }

  private _applyHeaderStyle(ws: XLSX.WorkSheet): void {
    const pastelRgb = this._getPastelPrimaryRgb();
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    for (let col = range.s.c; col <= range.e.c; col++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[addr]) continue;
      ws[addr].s = {
        fill: { patternType: 'solid', fgColor: { rgb: pastelRgb } },
        font: { bold: true },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  }

  private _autoFitColumns(ws: XLSX.WorkSheet): void {
    const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
    if (!data.length) return;
    const colCount = data.reduce((max, row) => Math.max(max, row.length), 0);
    const widths: { wch: number }[] = [];
    for (let col = 0; col < colCount; col++) {
      const maxLen = data.reduce((max, row) => {
        const cell = row[col];
        return Math.max(max, cell != null ? String(cell).length : 0);
      }, 0);
      widths.push({ wch: Math.min(Math.max(maxLen + 2, 10), 60) });
    }
    ws['!cols'] = widths;
  }
}
